import { useContext, useState } from 'react';
import dayjs from 'dayjs';
import { FaSpinner } from 'react-icons/fa';

import { LeftArrow } from '@benzinga/themed-icons';
import { SessionContext } from '@benzinga/session-context';
import { SubscriptionsManager, UserSubscription } from '@benzinga/subscription-manager';
import { Card, ShopManager } from '@benzinga/shop-manager';
import { getEndPeriodDate, getNewEndPeriodDate } from './CancelFlow';
import { OrderSummary } from './components';
import { TrackingManager } from '@benzinga/tracking-manager';

interface PauseFlowProps {
  creditCards: Card[];
  handleClose: () => void;
  setShowInitial: React.Dispatch<React.SetStateAction<boolean>>;
  setShowPauseFlow: React.Dispatch<React.SetStateAction<boolean>>;
  subscription: UserSubscription;
}

export const PauseFlow: React.FC<PauseFlowProps> = ({
  creditCards,
  handleClose,
  setShowInitial,
  setShowPauseFlow,
  subscription,
}) => {
  const [showReview, setShowReview] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);

  const [showError, setShowError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const session = useContext(SessionContext);
  const subscriptionManager = session.getManager(SubscriptionsManager);
  const shopManager = session.getManager(ShopManager);

  const handleGoBack = () => {
    setShowPauseFlow(false);
    setShowInitial(true);
  };

  const handlePauseSubmit = async () => {
    setShowError(false);
    try {
      setIsLoading(true);
      const deleteSub = await subscriptionManager.deleteSubscription(subscription.uuid);
      if (!deleteSub.ok) {
        const errorMessage = await (deleteSub.err?.data as Response)?.json();
        throw errorMessage;
      }

      const date = subscription.currentPeriodEnd ?? new Date();
      const reactivateDate = dayjs(date).startOf('day').add(2, 'month').format('YYYY-MM-DDThh:mm');
      const pauseSub = await shopManager.checkout(
        [],
        subscription.basePlan,
        '',
        subscription.coupon?.code ?? '',
        creditCards?.[0]?.uuid,
        reactivateDate,
        'new',
      );

      if (!pauseSub.ok) {
        await subscriptionManager.restartSubscription(subscription.uuid);
        const errorMessage = await (pauseSub.err?.data as Response)?.json();
        throw errorMessage;
      } else {
        await subscriptionManager.getSubscriptions(true);
        await session.getManager(TrackingManager).trackSubscriptionEvent('paused', {
          interval: subscription.interval,
          package_id: subscription.uuid,
          price: subscription.amount,
          product_id: subscription.product,
        });
        setShowConfirm(true);
        setShowReview(false);
        setIsLoading(false);
      }
    } catch (err) {
      console.log('Error pausing subscription', err);
      setShowError(true);
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 h-full overflow-y-auto">
      <div className="mb-2">
        <div className="font-bold text-lg">Pause for 2 Months</div>
        <p className="text-sm mb-2">Pause your subscription without being charged</p>
      </div>
      {showReview && (
        <div className="relative h-full text-base">
          <div className="flex flex-col justify-between h-full">
            <div>
              <h2>We&apos;re happy to see you stay!</h2>
              <OrderSummary checkout={null} discount={''} subscriptionToPause={subscription} title={'Pause Order'} />
              <p>
                Your subscription will be paused starting <strong>{getEndPeriodDate(subscription)}</strong> and will
                resume on <strong>{getNewEndPeriodDate(subscription, 'month')}</strong>. The next billing invoice will
                begin when your subscription is resumed.
              </p>
              <p>If you would like to unpause your subscription before then please contact our support team.</p>
            </div>
            <div>
              {showError && (
                <div className="text-red-500">
                  Something went wrong, please try again later or contact our Support team.
                </div>
              )}
              <button
                className={`px-4 py-2 mx-auto self-end mt-4 rounded-md w-full ${
                  !isLoading ? 'bg-bzblue-700 text-white' : 'bg-bzgrey-300/80 text-bzgrey-400'
                }`}
                disabled={isLoading}
                onClick={handlePauseSubmit}
              >
                {isLoading ? (
                  <div className="w-full flex justify-center">
                    <FaSpinner className="animate-spin mr-2 h-4 w-4 text-white my-1" />
                  </div>
                ) : (
                  'Confirm Pause Subscription'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {showConfirm && (
        <div className="flex flex-col justify-between h-full">
          <div>
            <div className="text-2xl py-2 font-bold mb-4">Your subscription is set to be paused.</div>
            <p>
              If you have any questions or would like to unpause your subscription, contact us and we would be happy to
              help.
            </p>
            <div>See you soon! &#128075; </div>
          </div>
          <button className="px-4 py-2 rounded-md border border-bzgrey-500/80 text-bzgrey-500/80" onClick={handleClose}>
            Close
          </button>
        </div>
      )}
      {/* Back button */}
      {!showConfirm && (
        <button className="absolute top-2 left-2" onClick={handleGoBack}>
          <LeftArrow />
        </button>
      )}
    </div>
  );
};
